<template>
  <RygenMultiSelect
    :id="id"
    :model-value="modelValue"
    :disabled="disabled"
    :placeholder="t('carriers.carriers', 2)"
    :options="carriers.data"
    :filter-match-mode="SERVER_SEARCH_MATCH_MODE"
    :filter-placeholder="
      t('common.findParam', { param: t('carriers.carriers') })
    "
    :empty-message="emptyMessage"
    :empty-filter-message="emptyMessage"
    option-label="scac"
    option-value="scac"
    data-key="id"
    class="carrier-multi-select"
    overlay-class="carrier-multi-select-overlay"
    display="chip"
    filter
    :show-toggle-all="allCarriersFitSinglePage"
    :virtual-scroller-options="{
      itemSize: 46,
      lazy: true,
      showLoader: true,
      loading: !carriers.data?.length && carriers.loading,
      onLazyLoad,
    }"
    @before-show="loadNextPage"
    @filter="onSearch($event.value)"
    @hide="onHide"
    @update:model-value="emit('update:model-value', $event)"
  >
    <template #value="{ value, placeholder }">
      <template v-if="!value?.length">{{ placeholder }}</template>
      <template v-else>
        <span v-for="scac in value" :key="scac" class="p-multiselect-chip-item">
          <Chip
            :label="scac"
            class="p-multiselect-chip"
            removable
            @remove="removeCarrier($event, scac)"
          />
        </span>
      </template>
    </template>

    <template #option="{ option }">
      {{ carrierLabel(option) }}
    </template>
  </RygenMultiSelect>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { SERVER_SEARCH_MATCH_MODE, useCarrierSearch } from '@/composables';
import { carrierLabel } from '@/util';

import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import Chip from 'primevue/chip';

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue?: string[];
    disabled?: boolean;
    /** Only offer carriers that can be tendered by email */
    hasEmailConfig?: boolean;
  }>(),
  { disabled: false, modelValue: undefined, hasEmailConfig: undefined },
);
const { id, hasEmailConfig, disabled, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: string[]): void;
}>();

const { t } = useI18n();
const {
  carriers,
  allCarriersFitSinglePage,
  loadNextPage,
  onLazyLoad,
  onSearch,
  onHide,
} = useCarrierSearch({ hasEmailConfig });

const emptyMessage = computed(() => {
  if (carriers.value.loading) {
    return t('common.loading');
  }

  return carriers.value.error
    ? t('errors.loadingParam', { param: t('carriers.carriers', 2) })
    : t('common.noParamMatchingSearch', { param: t('carriers.carriers', 2) });
});

/**
 * MultiSelect's built-in chip list keys each chip by
 * `<label looked up from options>_<index>`. Carrier search results are cleared
 * on closing the dropdown, so all chips resolve
 * to the same `undefined` label and collide on index alone; removing one
 * then makes Vue reuse its closed chip instance for whichever carrier
 * shifts into that key next, hiding it despite still being selected. Our
 * own `#value` slot keys each chip by its scac instead, so removals never
 * collide.
 */
const removeCarrier = (event: Event, scac: string) => {
  // MultiSelect's container opens the dropdown on click; stop it here the
  // same way the built-in chip removal does, or removing a chip reopens it.
  event.stopPropagation();
  emit(
    'update:model-value',
    (modelValue.value || []).filter((v) => v !== scac),
  );
};
</script>

<style>
.carrier-multi-select-overlay.p-multiselect-overlay {
  .p-multiselect-option {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}
</style>
